import { RabbitMQErrorMessage } from 'types/rabbitMQ';

export type ExceptionTypeGroup = {
  /** Clé de regroupement : message d'exception normalisé (lignes significatives, valeurs numériques neutralisées). */
  signature: string;
  /** Chaîne des classes d'exception, ex : "DbUpdateException → SqlException". */
  classes: string;
  /** Début du message d'exception pour identification visuelle. */
  excerpt: string;
  /** Messages du groupe avec leur index d'origine dans group.messages. */
  items: { message: RabbitMQErrorMessage; index: number }[];
};

const SANS_EXCEPTION = 'Sans exception';

/**
 * Normalise un message d'exception en signature de "type" :
 * on garde les 2 premières lignes significatives (exception externe + inner exception),
 * en neutralisant les valeurs numériques (ids, codes hexa...) pour que deux occurrences
 * de la même erreur se regroupent même si elles portent sur des données différentes.
 */
export function getExceptionSignature(exceptionMessage?: string | null): string {
  if (!exceptionMessage) return SANS_EXCEPTION;

  const lines = exceptionMessage
    .split('\n')
    .map(line => line.trim())
    .filter(line => line && !line.startsWith('at '));

  const signature = lines
    .slice(0, 2)
    .join(' ')
    .replace(/\d+/g, '#')
    .substring(0, 400);

  return signature || SANS_EXCEPTION;
}

function getExceptionClasses(exceptionMessage?: string | null): string {
  if (!exceptionMessage) return SANS_EXCEPTION;

  const matches = exceptionMessage.match(/[\w.]+(?:Exception|Error)\b/g) || [];
  const classes = Array.from(new Set(matches.map(c => c.split('.').pop() || c)));

  return classes.length > 0 ? classes.join(' → ') : SANS_EXCEPTION;
}

export function buildExceptionTypeGroups(messages: RabbitMQErrorMessage[]): ExceptionTypeGroup[] {
  const groups = new Map<string, ExceptionTypeGroup>();

  messages.forEach((message, index) => {
    const signature = getExceptionSignature(message.exceptionMessage);
    let group = groups.get(signature);

    if (!group) {
      group = {
        signature,
        classes: getExceptionClasses(message.exceptionMessage),
        excerpt: (message.exceptionMessage || '').split('\n')[0]?.trim().substring(0, 200) || '',
        items: []
      };
      groups.set(signature, group);
    }

    group.items.push({ message, index });
  });

  return Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
}
